import { Col, Row, Typography } from "antd";
import React from "react";
import { useWatch } from "react-hook-form";
import { ReactSelectField } from "../../atoms/FormElement";
import { InputNumberFieldV2 } from "../../atoms/FormElement/InputNumberField";

type PointOfInterestOption = {
  label: string;
  value: string;
};

type PointOfInterestFieldProps = {
  fieldName: string;
  label: string;
  control: any;
  setValue: any;
  options: PointOfInterestOption[];
  disabled?: boolean;
  validationProps?: Record<string, unknown>;
};

export const PointOfInterestField = ({
  fieldName,
  label,
  control,
  setValue,
  options,
  disabled,
  validationProps,
}: PointOfInterestFieldProps) => {
  const distanceFieldName = `${fieldName}Distances`;
  const selectedValue = useWatch({ control, name: fieldName });
  const distanceValues = useWatch({ control, name: distanceFieldName }) as Record<string, unknown> | undefined;
  const selectedOptions: PointOfInterestOption[] = Array.isArray(selectedValue)
    ? selectedValue
    : selectedValue
      ? [selectedValue]
      : [];

  const handleSelectionChange = (nextValue: PointOfInterestOption[] | PointOfInterestOption | null) => {
    const nextOptions = Array.isArray(nextValue) ? nextValue : nextValue ? [nextValue] : [];
    const previousDistances = distanceValues || {};
    const activeDistances = nextOptions.reduce<Record<string, unknown>>((result, option) => {
      const key = String(option.value);
      result[key] = previousDistances[key] ?? 0;
      return result;
    }, {});

    setValue(distanceFieldName, activeDistances);
  };

  return (
    <Col span={24} style={{ marginBottom: 8 }}>
      <div
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: "12px 16px 8px",
          background: "#fafafa",
        }}
      >
        <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
          {label}
        </Typography.Text>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Select nearby places and enter distances in metres.
        </Typography.Paragraph>

        <Col span={24} style={{ padding: 0, marginBottom: selectedOptions.length ? 16 : 0 }}>
          <ReactSelectField
            label="Select Nearby Places"
            fieldname={fieldName}
            control={control}
            setValue={setValue}
            selectOption={options}
            isDisabled={disabled}
            isMulti
            onChange={handleSelectionChange}
            iProps={{ placeholder: `Select ${label}` }}
            {...validationProps}
          />
        </Col>

        {selectedOptions.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              borderTop: "1px solid #f0f0f0",
              paddingTop: 12,
            }}
          >
            {selectedOptions.map((option) => {
              const distanceLabel = `${option.label} Distance (metres)`;

              return (
                <Row key={option.value} gutter={16} align="middle" style={{ width: "100%" }}>
                  <Col span={8}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 32,
                      }}
                    >
                      <Typography.Text style={{ marginBottom: 0, lineHeight: "32px" }}>
                        {distanceLabel}
                      </Typography.Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <InputNumberFieldV2
                      fieldname={`${distanceFieldName}.${option.value}` as any}
                      control={control}
                      disabled={disabled}
                      defaultValue={0}
                      formItemProps={{
                        style: { marginBottom: 0 },
                      }}
                      inputNumberProps={{
                        min: 0,
                        placeholder: distanceLabel,
                        style: { width: "100%" },
                      }}
                    />
                  </Col>
                </Row>
              );
            })}
          </div>
        ) : null}
      </div>
    </Col>
  );
};
